import { render } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  Activity,
  BadgeDollarSign,
  CircleDot,
  Calculator,
  Clock,
  Crosshair,
  Crown,
  DatabaseZap,
  Handshake,
  Hourglass,
  Info,
  MonitorDot,
  Moon,
  Pause,
  Radio,
  Repeat,
  ShieldCheck,
  Sun,
  Swords,
  TrendingDown,
  TrendingUp,
  Trophy,
  UserMinus,
  Zap
} from "lucide-preact";
import { useFantasyData } from "./useFantasyData";
import type {
  MatchPlayerDetail,
  RoleArchetype,
  SessionEndReason,
  SessionMatch
} from "./types";
import "./styles.css";

type Theme = "light" | "dark" | "auto";

const readStoredTheme = (): Theme => {
  if (typeof localStorage === "undefined") return "auto";
  const stored = localStorage.getItem("rlft-theme");
  if (stored === "light" || stored === "dark" || stored === "auto") return stored;
  return "auto";
};

const applyTheme = (theme: Theme) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "auto") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
};

const useTheme = () => {
  const [theme, setTheme] = useState<Theme>(() => {
    const t = readStoredTheme();
    applyTheme(t);
    return t;
  });

  useEffect(() => {
    applyTheme(theme);
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("rlft-theme", theme);
    }
  }, [theme]);

  const cycle = () => {
    setTheme((prev) => {
      const order: Theme[] = ["auto", "light", "dark"];
      return order[(order.indexOf(prev) + 1) % order.length];
    });
  };

  return { theme, cycle };
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    maximumFractionDigits: 1
  }).format(value);

const formatTime = (value: string) =>
  new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));

const formatAgo = (value: string) => {
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(value)) / 1000));
  if (seconds < 60) return `vor ${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `vor ${minutes}m`;
  return `vor ${Math.round(minutes / 60)}h`;
};

const formatPercent = (delta: number, base: number) => {
  const percent = base > 0 ? (delta / Math.max(base - delta, 1)) * 100 : 0;
  return `${percent >= 0 ? "+" : ""}${percent.toFixed(1)}%`;
};

const formBadge = (label: string | null, streak: number) => {
  if (label === "heater") return { icon: "🔥🔥", text: `Heater ${streak}W`, css: "form-badge form-heater" };
  if (label === "in_form") return { icon: "🔥", text: `In Form ${streak}W`, css: "form-badge form-hot" };
  if (label === "frozen") return { icon: "❄️❄️", text: `Frozen ${Math.abs(streak)}L`, css: "form-badge form-frozen" };
  if (label === "cold") return { icon: "❄️", text: `Cold ${Math.abs(streak)}L`, css: "form-badge form-cold" };
  return null;
};

const bestBy = <T,>(items: T[], score: (item: T) => number) =>
  items.reduce<T | null>((best, item) => (best === null || score(item) > score(best) ? item : best), null);

const endReasonLabel = (reason: SessionEndReason): { text: string; icon: typeof Hourglass } | null => {
  if (reason === "idle_gap") return { text: "15min Idle", icon: Hourglass };
  if (reason === "regular_absent") return { text: "Crew zerbrochen", icon: UserMinus };
  if (reason === "duration_cap") return { text: "4h Cap", icon: Clock };
  return null;
};

const archetypeLabel = (a: RoleArchetype): { text: string; emoji: string; css: string } | null => {
  if (a === "striker") return { text: "Striker", emoji: "⚽", css: "role-archetype role-striker" };
  if (a === "playmaker") return { text: "Playmaker", emoji: "🎯", css: "role-archetype role-playmaker" };
  if (a === "wall") return { text: "Wall", emoji: "🛡️", css: "role-archetype role-wall" };
  if (a === "allrounder") return { text: "Allrounder", emoji: "🌀", css: "role-archetype role-allrounder" };
  return null;
};

// Translate σ into a 3-step visual label. Lower σ = more reliable.
const consistencyLabel = (sigma: number | null): { text: string; css: string } | null => {
  if (sigma === null) return null;
  if (sigma <= 7) return { text: "Solid", css: "consistency-pill cons-solid" };
  if (sigma <= 12) return { text: "Steady", css: "consistency-pill cons-steady" };
  return { text: "Wildcard", css: "consistency-pill cons-wild" };
};

// SessionTimeline: a tiny strip of match-dots per regular for the selected
// session. Each row is a regular, each column a match — solid dot = present,
// hollow dot = absent. Lets you eyeball who dropped out and when.
type TimelineProps = {
  matches: SessionMatch[];
  regulars: Array<{ player_id: string; player_name: string; is_owner: boolean }>;
};
const SessionTimeline = ({ matches, regulars }: TimelineProps) => {
  if (matches.length === 0 || regulars.length === 0) return null;
  return (
    <div class="session-timeline">
      <div class="timeline-seq-row">
        <span class="timeline-axis-label">&nbsp;</span>
        {matches.map((m) => (
          <span class="timeline-seq" key={m.match_id} title={`Match ${m.match_seq}`}>
            M{m.match_seq}
          </span>
        ))}
      </div>
      {regulars.map((r) => (
        <div class="timeline-player-row" key={r.player_id}>
          <span class="timeline-axis-label" title={r.player_name}>
            {r.is_owner && <Crown size={12} />} {r.player_name}
          </span>
          {matches.map((m) => {
            const present = m.present_player_ids.includes(r.player_id);
            return (
              <span
                class={`timeline-cell ${present ? "is-present" : "is-absent"}`}
                key={m.match_id}
                title={present ? "Dabei" : "Nicht dabei"}
              >
                <i class="dot" />
              </span>
            );
          })}
        </div>
      ))}
      <div class="timeline-score-row">
        <span class="timeline-axis-label">Score</span>
        {matches.map((m) => {
          const ownerWon = m.owner_team === "blue"
            ? m.blue_score > m.orange_score
            : m.owner_team === "orange"
              ? m.orange_score > m.blue_score
              : null;
          const score = m.owner_team === "orange"
            ? `${m.orange_score}:${m.blue_score}`
            : `${m.blue_score}:${m.orange_score}`;
          return (
            <span class={`timeline-score ${ownerWon === true ? "win" : ownerWon === false ? "loss" : ""}`} key={m.match_id}>
              {score}
            </span>
          );
        })}
      </div>
    </div>
  );
};

// Aggregate opponent rows for the OpponentsBlock. In "match" mode we show
// the opponents from the latest match only; in "session" mode we aggregate
// over the whole session using the encounters counter.
type OpponentAggregate = {
  player_id: string;
  player_name: string;
  encounters: number;
  // Stats for the rendered match (latest in "match" mode, best in "session" mode)
  goals: number;
  assists: number;
  saves: number;
  fp: number;
  won: boolean | null;
  match_seq: number | null;
};

const opponentsForLatestMatch = (
  matchPlayers: MatchPlayerDetail[],
  sessionMatches: SessionMatch[]
): { owner: MatchPlayerDetail | null; opponents: OpponentAggregate[]; match: SessionMatch | null; encountersById: Map<string, number> } => {
  const encountersById = new Map<string, number>();
  for (const row of matchPlayers) {
    if (row.player_role === "owner") continue;
    // count vs-owner per (player, match) — we use sessionMatches to learn
    // owner_team per match. We do a fresh pass below.
  }
  const ownerTeamByMatch = new Map<string, "blue" | "orange" | null>();
  for (const m of sessionMatches) ownerTeamByMatch.set(m.match_id, m.owner_team);
  for (const row of matchPlayers) {
    if (row.player_role === "owner") continue;
    const ot = ownerTeamByMatch.get(row.match_id) ?? null;
    if (!ot || row.team === "unknown") continue;
    if (row.team !== ot) {
      encountersById.set(row.player_id, (encountersById.get(row.player_id) ?? 0) + 1);
    }
  }

  const latest = sessionMatches.length > 0 ? sessionMatches[sessionMatches.length - 1] : null;
  if (!latest) return { owner: null, opponents: [], match: null, encountersById };
  const inLatest = matchPlayers.filter((r) => r.match_id === latest.match_id);
  const owner = inLatest.find((r) => r.player_role === "owner") ?? null;
  const opps = inLatest
    .filter((r) => r.player_role !== "owner" && latest.owner_team !== null && r.team !== latest.owner_team && r.team !== "unknown")
    .map<OpponentAggregate>((r) => ({
      player_id: r.player_id,
      player_name: r.player_name,
      encounters: encountersById.get(r.player_id) ?? 1,
      goals: r.goals,
      assists: r.assists,
      saves: r.saves,
      fp: r.fantasy_points,
      won: r.won,
      match_seq: r.match_seq
    }))
    .sort((a, b) => b.fp - a.fp);
  return { owner, opponents: opps, match: latest, encountersById };
};

const opponentsForSession = (
  matchPlayers: MatchPlayerDetail[],
  sessionMatches: SessionMatch[]
): OpponentAggregate[] => {
  const ownerTeamByMatch = new Map<string, "blue" | "orange" | null>();
  for (const m of sessionMatches) ownerTeamByMatch.set(m.match_id, m.owner_team);
  const acc = new Map<string, OpponentAggregate>();
  for (const row of matchPlayers) {
    if (row.player_role === "owner") continue;
    const ot = ownerTeamByMatch.get(row.match_id) ?? null;
    if (!ot || row.team === "unknown" || row.team === ot) continue;
    const cur = acc.get(row.player_id);
    if (!cur) {
      acc.set(row.player_id, {
        player_id: row.player_id,
        player_name: row.player_name,
        encounters: 1,
        goals: row.goals,
        assists: row.assists,
        saves: row.saves,
        fp: row.fantasy_points,
        won: row.won,
        match_seq: row.match_seq
      });
      continue;
    }
    cur.encounters += 1;
    // Keep the *best* match (highest FP) as the displayed snapshot — the
    // user wants to see how hard this opponent peaked against them.
    if (row.fantasy_points > cur.fp) {
      cur.goals = row.goals;
      cur.assists = row.assists;
      cur.saves = row.saves;
      cur.fp = row.fantasy_points;
      cur.won = row.won;
      cur.match_seq = row.match_seq;
    }
  }
  return Array.from(acc.values()).sort((a, b) => b.fp - a.fp);
};

const App = () => {
  const {
    session,
    sessions,
    sessionMatches,
    sessionMatchPlayers,
    selectedSessionId,
    selectSession,
    activeSessionPlayerIds,
    currentLobbyPlayerIds,
    events,
    devices,
    ingestEvents,
    activeDevices,
    mvp,
    loading,
    error,
    isMock
  } = useFantasyData();
  const { theme, cycle: cycleTheme } = useTheme();
  const [opponentsMode, setOpponentsMode] = useState<"match" | "session">("match");
  const themeLabel = theme === "auto" ? "System-Theme" : theme === "dark" ? "Dark Mode" : "Light Mode";
  const ThemeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : MonitorDot;
  const latestIngest = ingestEvents[0];
  const topScorer = bestBy(session.players, (player) => player.goals);
  const playmaker = bestBy(session.players, (player) => player.assists);
  const wall = bestBy(session.players, (player) => player.saves);
  const stockUp = bestBy(session.players, (player) => player.market_value_delta);
  const awards = [
    {
      label: "MVP",
      player: mvp,
      value: `${mvp?.fm_score.toFixed(1) ?? "0.0"} FM`,
      icon: <Crown size={20} />
    },
    {
      label: "Sniper",
      player: topScorer,
      value: `${topScorer?.goals ?? 0} Goals`,
      icon: <Crosshair size={20} />
    },
    {
      label: "Playmaker",
      player: playmaker,
      value: `${playmaker?.assists ?? 0} Assists`,
      icon: <Handshake size={20} />
    },
    {
      label: "Wall",
      player: wall,
      value: `${wall?.saves ?? 0} Saves`,
      icon: <ShieldCheck size={20} />
    },
    {
      label: "Stock Up",
      player: stockUp,
      value: stockUp ? formatPercent(stockUp.market_value_delta, stockUp.current_market_value) : "+0.0%",
      icon: <BadgeDollarSign size={20} />
    }
  ];

  // Regulars strip for the SessionTimeline (sorted by first-seen so the
  // order matches the timeline columns). Owner always first so the user
  // recognises their own row at a glance.
  const timelineRegulars = useMemo(() => {
    const ids = new Set<string>(session.regular_player_ids);
    const ownerPlayer = session.players.find((p) => p.role === "owner");
    const out: Array<{ player_id: string; player_name: string; is_owner: boolean }> = [];
    if (ownerPlayer) {
      out.push({ player_id: ownerPlayer.player_id, player_name: ownerPlayer.player_name, is_owner: true });
    }
    const others = session.players
      .filter((p) => ids.has(p.player_id) && p.role !== "owner")
      .sort((a, b) => (a.first_seen_seq ?? 9999) - (b.first_seen_seq ?? 9999));
    for (const p of others) {
      out.push({ player_id: p.player_id, player_name: p.player_name, is_owner: false });
    }
    return out;
  }, [session.regular_player_ids, session.players]);

  const opponentLatest = useMemo(
    () => opponentsForLatestMatch(sessionMatchPlayers, sessionMatches),
    [sessionMatchPlayers, sessionMatches]
  );
  const opponentSession = useMemo(
    () => opponentsForSession(sessionMatchPlayers, sessionMatches),
    [sessionMatchPlayers, sessionMatches]
  );
  const opponentRows = opponentsMode === "match" ? opponentLatest.opponents : opponentSession;
  const opponentEncountersById = opponentLatest.encountersById;
  // Reference FP for the bar chart: the best (highest-FP) of owner + crew in
  // the relevant match, so opponents are visually compared to "your side".
  const opponentBenchmark = useMemo(() => {
    if (opponentsMode === "match") {
      const latest = sessionMatches[sessionMatches.length - 1];
      if (!latest) return 0;
      const crew = sessionMatchPlayers.filter(
        (r) => r.match_id === latest.match_id && r.player_role !== "guest" && r.team === latest.owner_team
      );
      const max = bestBy(crew, (r) => r.fantasy_points);
      return Math.max(max?.fantasy_points ?? 0, 1);
    }
    // session mode: use the best opponent FP itself for relative scaling
    const top = opponentRows[0]?.fp ?? 0;
    return Math.max(top, 1);
  }, [opponentsMode, opponentRows, sessionMatchPlayers, sessionMatches]);

  return (
    <main class="app-shell">
      <section class="topbar">
        <div>
          <p class="eyebrow">Rocket League Fantasy</p>
          <h1>Session Command Center</h1>
        </div>
        <button
          type="button"
          class="theme-toggle"
          onClick={cycleTheme}
          title={`${themeLabel} (Klick: nächster Modus)`}
          aria-label={`Theme wechseln — derzeit ${themeLabel}`}
        >
          <ThemeIcon size={20} />
        </button>
        <div class="live-pill" title="Realtime status">
          <Radio size={18} />
          {isMock ? "Demo Mode" : "Supabase Live"}
        </div>
      </section>

      {isMock && (
        <div class="banner banner-warn">
          Demo Mode aktiv. Setze <code>VITE_SUPABASE_URL</code>, <code>VITE_SUPABASE_ANON_KEY</code> und{" "}
          <code>VITE_GROUP_ID</code> (siehe <code>apps/web/.env.example</code>), um Live-Daten zu sehen.
        </div>
      )}
      {error && <div class="banner banner-error">Supabase: {error}</div>}
      {loading && <div class="banner">Live-Daten werden geladen...</div>}
      {!isMock && !loading && session.match_count === 0 && (
        <div class="banner banner-info">
          Noch keine Matches in dieser Gruppe. Starte den Companion und spiele ein Match — dieses Dashboard
          aktualisiert sich live.
        </div>
      )}

      <section class="hero-grid">
        <article class="mvp-panel">
          <div class="panel-heading">
            <Crown size={22} />
            <span>Live MVP</span>
          </div>
          <h2>{mvp?.player_name ?? "Noch offen"}</h2>
          <div class="mvp-value">{mvp ? formatCurrency(mvp.current_market_value) : "-"}</div>
          <div class="metric-row">
            <span title="FM-Score = FP/M × Rolle × Konsistenz × Synergie">{mvp?.fm_score.toFixed(1) ?? "0.0"} FM</span>
            <span title="Reine Fantasy Points pro Match (geglättet)">{mvp?.effective_fppm.toFixed(1) ?? "0.0"} FP/M</span>
            <span>{mvp ? formatPercent(mvp.market_value_delta, mvp.current_market_value) : "+0.0%"}</span>
            <span>Rating {mvp?.current_rating.toFixed(1) ?? "0.0"}</span>
          </div>
        </article>

        <article class="session-panel">
          <div class="panel-heading">
            <Activity size={22} />
            <span>Sessions</span>
          </div>
          <div class="session-stats compact">
            <div>
              <strong>{session.match_count}</strong>
              <span>Matches</span>
            </div>
            <div>
              <strong>{formatTime(session.started_at)}</strong>
              <span>Start</span>
            </div>
            <div>
              <strong class={session.status === "active" ? "status-live" : ""}>
                {session.status === "active" ? "Live" : "Fertig"}
              </strong>
              <span>Status</span>
            </div>
          </div>

          <div class="sessions-list">
            <div class="sessions-list-head">
              <strong>Letzte Sessions</strong>
              {selectedSessionId && (
                <button type="button" class="sessions-list-reset" onClick={() => selectSession(null)}>
                  Aktuelle zeigen
                </button>
              )}
            </div>
            {sessions.length === 0 && <span class="sessions-empty">Noch keine Sessions</span>}
            {sessions.map((s) => {
              const isSelected = selectedSessionId
                ? s.id === selectedSessionId
                : s.id === session.session_id;
              const isCurrent = s.id === session.session_id;
              const endTag = endReasonLabel(s.end_reason);
              const EndIcon = endTag?.icon;
              return (
                <div class={`session-block ${isSelected ? "is-selected" : ""}`} key={s.id}>
                  <button
                    type="button"
                    class={`session-row ${isSelected ? "is-selected" : ""}`}
                    onClick={() => selectSession(s.id)}
                  >
                    <i class={s.status === "active" ? "dot dot-live" : "dot"} />
                    <div class="session-row-main">
                      <strong>{formatTime(s.started_at)}</strong>
                      <span>
                        {s.match_count} {s.match_count === 1 ? "Match" : "Matches"}
                        {s.mvp_name && <> · MVP {s.mvp_name}</>}
                      </span>
                    </div>
                    {endTag && EndIcon && (
                      <span
                        class={`end-reason-pill end-reason-${s.end_reason}`}
                        title={`Session-Ende: ${endTag.text}`}
                      >
                        <EndIcon size={12} />
                        <span>{endTag.text}</span>
                      </span>
                    )}
                    <span class="session-row-meta">
                      {s.status === "active" ? "live" : formatAgo(s.started_at)}
                    </span>
                  </button>
                  {isCurrent && timelineRegulars.length > 0 && sessionMatches.length > 0 && (
                    <SessionTimeline matches={sessionMatches} regulars={timelineRegulars} />
                  )}
                </div>
              );
            })}
          </div>

          <div class="device-status">
            <div class="device-status-head">
              <MonitorDot size={18} />
              <strong>Companion</strong>
            </div>
            <div class="device-status-count">
              {activeDevices.length}/{devices.length || 1} online
            </div>
            <div class="device-list">
              {devices.length === 0 ? (
                <span>Kein Companion verbunden</span>
              ) : (
                devices.map((device) => (
                  <span class="device-row" key={device.id}>
                    <i class={activeDevices.some((active) => active.id === device.id) ? "dot dot-live" : "dot"} />
                    {device.device_name} · {formatAgo(device.last_seen_at)}
                  </span>
                ))
              )}
            </div>
          </div>
        </article>
      </section>

      <section class="awards-grid">
        {awards.map((award) => (
          <article class="award-card" key={award.label}>
            <div class="award-icon">{award.icon}</div>
            <span>{award.label}</span>
            <strong>{award.player?.player_name ?? "-"}</strong>
            <em>{award.value}</em>
          </article>
        ))}
      </section>

      <section class="content-grid">
        <div class="leaderboard">
          <div class="section-title">
            <Trophy size={20} />
            <h2>Fantasy Markt</h2>
            <span class="crew-tag">Crew · {session.players.length}</span>
            <span class="info-tip" tabIndex={0} aria-label="Wie Fantasy Points berechnet werden">
              <Info size={16} />
              <div class="tooltip" role="tooltip">
                <strong>Sortierung: FM-Score (Phase 1)</strong>
                <p class="tooltip-foot" style={{ marginTop: "4px" }}>
                  FM = FP/M &times; Rolle &times; Konsistenz &times; Synergie. Rolle: +5% wenn Archetyp klar ist (Striker/Playmaker/Wall). Konsistenz: ±8% basierend auf σ (niedriger = verlässlicher, Baseline 10). Synergie: ±8% basierend auf Win-Rate-Delta wenn auf Owner-Team.
                </p>
                <div class="tooltip-divider" />
                <strong>FP/M (Grundlage)</strong>
                <p class="tooltip-foot" style={{ marginTop: "4px" }}>
                  Score = (Total FP + 35 &times; 3) &divide; (Matches + 3). Neue Spieler werden zur Baseline 35 geschrinkt — Lucky-Single-Games dominieren nicht.
                </p>
                <div class="tooltip-divider" />
                <strong>Fantasy Points pro Match</strong>
                <div class="tooltip-divider" />
                <div class="tooltip-row"><span>Tor</span><span>+10</span></div>
                <div class="tooltip-row"><span>Assist</span><span>+7</span></div>
                <div class="tooltip-row"><span>Save</span><span>+6</span></div>
                <div class="tooltip-row"><span>Shot</span><span>+1.5</span></div>
                <div class="tooltip-row"><span>Demo</span><span>+1.25</span></div>
                <div class="tooltip-row"><span>Score &divide; 100</span><span>+ ~5 /Match</span></div>
                <div class="tooltip-row"><span>Sieg</span><span>+8</span></div>
                <div class="tooltip-row"><span>Niederlage</span><span>&minus;2</span></div>
                <div class="tooltip-row"><span>Eigentor</span><span>&minus;5</span></div>
                <div class="tooltip-divider" />
                <strong>Engine v3 Bonus</strong>
                <div class="tooltip-row"><span>Comeback (2+ Tore zurück)</span><span>+12</span></div>
                <div class="tooltip-row"><span>Overtime-Sieg</span><span>+6</span></div>
                <div class="tooltip-row"><span>Sweep-Margin</span><span>bis +5</span></div>
                <div class="tooltip-row"><span>Touches-Efficiency</span><span>bis +6</span></div>
                <div class="tooltip-row"><span>Ball-Padding</span><span>&minus;10</span></div>
                <div class="tooltip-divider" />
                <p class="tooltip-foot">
                  Marktwert bewegt sich pro Match via z-Score (Performance vs Erwartung), Form-Streak und Underdog/Favorit-Faktor. Forfeit unter 90s &times; 0.4.
                </p>
              </div>
            </span>
          </div>
          {session.players.length === 0 && (
            <p class="leaderboard-empty">
              Noch keine Crew-Mitglieder erkannt. Spiele 1-2 Matches mit Freunden — sie werden automatisch erkannt sobald sie mindestens einmal auf deinem Team waren.
            </p>
          )}
          <div class="player-list">
            {session.players
              .slice()
              .sort((a, b) => {
                // In-lobby players first, then everyone else by FM-score.
                const aLive = currentLobbyPlayerIds.has(a.player_id) ? 1 : 0;
                const bLive = currentLobbyPlayerIds.has(b.player_id) ? 1 : 0;
                if (aLive !== bLive) return bLive - aLive;
                return b.fm_score - a.fm_score;
              })
              .map((player, index) => {
              const badge = formBadge(player.form_label, player.current_streak);
              const archetype = archetypeLabel(player.role_archetype);
              const consistency = consistencyLabel(player.consistency_sigma);
              const lobbyKnown = currentLobbyPlayerIds.size > 0;
              const inLobby = currentLobbyPlayerIds.has(player.player_id);
              const inSession = activeSessionPlayerIds.has(player.player_id);
              const isOwner = player.role === "owner";
              // Pause vs Raus:
              //  * absent_streak == 1 — single missed match → "Pause"
              //  * absent_streak == 2 — last warning before split → "Pause 2M"
              //  * lobby-data says they're gone but constellation hasn't caught
              //    up yet → fall back to "Verlassen"
              const isAbsent = lobbyKnown && inSession && !inLobby;
              const pauseStreak = player.is_regular ? player.absent_streak : 0;
              return (
                <article class={`player-card ${inLobby ? "is-live" : ""} ${isAbsent || pauseStreak > 0 ? "is-absent" : ""} ${isOwner ? "is-owner" : ""}`} key={player.player_id}>

                  <div class="rank">{index + 1}</div>
                  <div class="player-main">
                    <div class="player-name-row">
                      <span class="player-name">{player.player_name}</span>
                      {isOwner && <span class="role-pill role-owner" title="Device-Owner — du">Du</span>}
                      {player.is_regular && !isOwner && (
                        <span class="role-pill role-regular" title="Regular dieser Session — ≥2 konsekutive Matches mit dir">
                          Regular
                        </span>
                      )}
                      {inLobby && <span class="live-pill-small" title="Im aktuellen Match-Roster">Live</span>}
                      {pauseStreak === 1 && !inLobby && (
                        <span class="pause-pill pause-1" title="1 Match nicht dabei">
                          <Pause size={11} /> Pause
                        </span>
                      )}
                      {pauseStreak === 2 && !inLobby && (
                        <span class="pause-pill pause-2" title="2 Matches in Folge nicht dabei — beim 3. wird die Session geteilt">
                          <Pause size={11} /> Pause 2M
                        </span>
                      )}
                      {isAbsent && pauseStreak === 0 && (
                        <span class="absent-pill" title="War in der Session, aktuell nicht mehr in der Lobby">
                          Verlassen
                        </span>
                      )}
                      <span class="tier-pill">{player.tier} · {player.current_rating.toFixed(1)}</span>
                      {archetype && (
                        <span
                          class={archetype.css}
                          title={`Rolle aus Lifetime-Stats abgeleitet · G ${(player.role_weights.goals ?? 0).toFixed(2)} · A ${(player.role_weights.assists ?? 0).toFixed(2)} · S ${(player.role_weights.saves ?? 0).toFixed(2)}`}
                        >
                          <span aria-hidden>{archetype.emoji}</span>
                          <span>{archetype.text}</span>
                        </span>
                      )}
                      {consistency && (
                        <span
                          class={consistency.css}
                          title={`Konsistenz σ ${player.consistency_sigma?.toFixed(1) ?? "?"} (Streuung der letzten Performance-Scores). Niedriger = verlässlicher.`}
                        >
                          {consistency.text}
                        </span>
                      )}
                      {badge && (
                        <span class={badge.css}>
                          <span aria-hidden>{badge.icon}</span>
                          <span>{badge.text}</span>
                        </span>
                      )}
                    </div>
                    <div class="player-sub">
                      {player.goals} G · {player.assists} A · {player.saves} S · {player.wins}/{player.matches} W
                      <> · {player.fantasy_points.toFixed(0)} FP total</>
                      {player.peak_market_value > player.current_market_value && (
                        <> · ATH {formatCurrency(player.peak_market_value)}</>
                      )}
                    </div>
                  </div>
                  <div
                    class="player-score"
                    title={
                      `FM ${player.fm_score.toFixed(1)} = FP/M ${player.effective_fppm.toFixed(1)}` +
                      ` · Rolle ×${player.fm_role_mult.toFixed(2)}` +
                      ` · Konsistenz ×${player.fm_consistency_mult.toFixed(2)}` +
                      ` · Synergie ×${player.fm_synergy_mult.toFixed(2)}`
                    }
                  >
                    <strong>{player.fm_score.toFixed(1)}</strong>
                    <span>FM-Score</span>
                  </div>
                  <div class={`trend trend-${player.trend}`}>
                    {player.market_value_delta >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    <span>{formatPercent(player.market_value_delta, player.current_market_value)}</span>
                  </div>
                  <div class="market-value">{formatCurrency(player.current_market_value)}</div>
                </article>
              );
            })}
          </div>

          <div class="opponents-block">
            <div class="section-title opponents-title">
              <Swords size={18} />
              <h2>Begegnungen</h2>
              <span class="crew-tag">
                {opponentsMode === "match" ? "Letztes Match" : `${opponentRows.length} ges.`}
              </span>
              <div class="opponents-toggle" role="tablist" aria-label="Gegner-Modus">
                <button
                  type="button"
                  role="tab"
                  aria-selected={opponentsMode === "match"}
                  class={opponentsMode === "match" ? "is-active" : ""}
                  onClick={() => setOpponentsMode("match")}
                >
                  Match
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={opponentsMode === "session"}
                  class={opponentsMode === "session" ? "is-active" : ""}
                  onClick={() => setOpponentsMode("session")}
                >
                  Session
                </button>
              </div>
            </div>
            {opponentRows.length === 0 ? (
              <p class="opponents-empty">
                {opponentsMode === "match"
                  ? "Keine Gegner im letzten Match — oder das Match wurde gegen die Crew gespielt."
                  : "Noch keine Gegner in dieser Session erfasst."}
              </p>
            ) : (
              <div class="opponents-list">
                {opponentRows.map((opp) => {
                  const enc = opponentsMode === "session" ? opp.encounters : opponentEncountersById.get(opp.player_id) ?? 0;
                  const recurring = enc >= 2;
                  const pct = Math.min(100, Math.max(4, Math.round((opp.fp / opponentBenchmark) * 100)));
                  return (
                    <article class="opponent-row" key={opp.player_id}>
                      <div class="opponent-head">
                        <span class="opponent-name">{opp.player_name}</span>
                        {recurring && (
                          <span class="recurring-pill" title={`In dieser Session ${enc}× getroffen`}>
                            <Repeat size={11} /> {enc}× Session
                          </span>
                        )}
                        {opp.won === true && opponentsMode === "match" && (
                          <span class="opp-result-pill won" title="Hat dieses Match gewonnen">Sieger</span>
                        )}
                        {opp.match_seq !== null && opponentsMode === "session" && (
                          <span class="opp-match-tag" title={`Bestes Match dieser Session: M${opp.match_seq}`}>
                            Peak in M{opp.match_seq}
                          </span>
                        )}
                      </div>
                      <div class="opponent-stats">
                        <span>{opp.goals}G</span>
                        <span>{opp.assists}A</span>
                        <span>{opp.saves}S</span>
                        <span class="opponent-fp">{opp.fp.toFixed(0)} FP</span>
                      </div>
                      <div class="opponent-bar-track" aria-label={`${opp.fp.toFixed(0)} FP vs. Benchmark ${opponentBenchmark.toFixed(0)} FP`}>
                        <div class="opponent-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                    </article>
                  );
                })}
                {opponentsMode === "match" && opponentLatest.owner && (
                  <article class="opponent-row is-self" title="Deine eigene Performance in diesem Match — als Referenz">
                    <div class="opponent-head">
                      <span class="opponent-name">{opponentLatest.owner.player_name} <span class="opp-self-tag">Du</span></span>
                    </div>
                    <div class="opponent-stats">
                      <span>{opponentLatest.owner.goals}G</span>
                      <span>{opponentLatest.owner.assists}A</span>
                      <span>{opponentLatest.owner.saves}S</span>
                      <span class="opponent-fp">{opponentLatest.owner.fantasy_points.toFixed(0)} FP</span>
                    </div>
                    <div class="opponent-bar-track">
                      <div
                        class="opponent-bar-fill is-self"
                        style={{ width: `${Math.min(100, Math.round((opponentLatest.owner.fantasy_points / opponentBenchmark) * 100))}%` }}
                      />
                    </div>
                  </article>
                )}
              </div>
            )}
          </div>
        </div>

        <aside class="feed">
          <div class="pipeline-panel">
            <div class="section-title">
              <DatabaseZap size={20} />
              <h2>Pipeline</h2>
            </div>
            <div class="pipeline-grid">
              <div>
                <strong>{latestIngest?.event_type ?? "idle"}</strong>
                <span>Letztes Event</span>
              </div>
              <div>
                <strong>{latestIngest?.players_count ?? 0}</strong>
                <span>Spieler</span>
              </div>
              <div>
                <strong>{latestIngest?.accepted === false ? "Fehler" : "OK"}</strong>
                <span>Ingest</span>
              </div>
            </div>
            <div class="ingest-list">
              {ingestEvents.map((event) => (
                <span class="ingest-row" key={event.id}>
                  <i class={event.accepted ? "dot dot-live" : "dot dot-error"} />
                  {event.event_type} · {event.players_count}P · {formatAgo(event.observed_at)}
                </span>
              ))}
            </div>
          </div>

          <div class="formula-panel">
            <div class="section-title">
              <Calculator size={20} />
              <h2>Formel</h2>
            </div>
            <div class="formula-list">
              <span title="Goal × 10">Goal +10</span>
              <span title="Assist × 7">Assist +7</span>
              <span title="Save × 6">Save +6</span>
              <span title="Shot × 1.5">Shot +1.5</span>
              <span title="Demo × 1.25">Demo +1.25</span>
              <span title="Score / 100 — Punkteanteil aus dem RL-Scoreboard">Score ÷100</span>
              <span title="Win-Bonus">Win +8</span>
              <span title="Niederlage-Strafe">Loss −2</span>
              <span title="Eigentor">Own −5</span>
              <span title="Comeback aus 2+ Tore Rückstand">Comeback +12</span>
              <span title="Overtime-Sieg">OT-Sieg +6</span>
              <span title="Bis +5 Punkte je nach Tor-Differenz">Sweep +5</span>
              <span title="Bis +6 Punkte für Goals/Touches Ratio">Clinic +6</span>
              <span title="Ball-Padding: hoher Score, keine Actions">Pad −10</span>
              <span title="Markt-Schwung pro Match bis ±22%">Markt ±22%</span>
            </div>
          </div>

          <div class="section-title">
            <Zap size={20} />
            <h2>Live Feed</h2>
          </div>
          {events.map((event) => (
            <article class="feed-item" key={event.id}>
              <CircleDot size={16} />
              <div>
                <strong>{event.label}</strong>
                <p>{event.detail}</p>
                <time>{formatTime(event.created_at)}</time>
              </div>
            </article>
          ))}
        </aside>
      </section>
    </main>
  );
};

render(<App />, document.getElementById("app")!);
